if (typeof FlourishBundleBuilder !== 'function') {
  class FlourishBundleBuilder extends HTMLElement {
    constructor() {
      super();
      this._packSize = 4;
      this._selections = [];
      this._mobileBarHome = null;
    }

    connectedCallback() {
      this._scope = this.closest('.shopify-section') || this;
      this._packButtons = this.querySelectorAll('[data-pack-size]');
      this._slotGrids = this._scope.querySelectorAll('[data-bundle-slots]');
      this._ctaButtons = this._scope.querySelectorAll('[data-bundle-cta]');
      this._productCards = this._scope.querySelectorAll('[data-bundle-add]');
      this._mobileBar = this._scope.querySelector('[data-bundle-mobile-bar]');
      this._maxPackSize = parseInt(this.dataset.maxPackSize, 10) || 8;
      this._packSize = parseInt(this.dataset.defaultPackSize, 10) || 4;
      this._cartAddUrl = `${window.KROWN?.settings?.routes?.cart_add_url || '/cart/add'}.js`;

      this._onPackClick = this._onPackClick.bind(this);
      this._onAddClick = this._onAddClick.bind(this);
      this._onSlotClick = this._onSlotClick.bind(this);
      this._onCtaClick = this._onCtaClick.bind(this);
      this._onMobileBarMediaChange = this._onMobileBarMediaChange.bind(this);
      this._mobileBarMedia = window.matchMedia('(max-width: 1023px)');

      this._packButtons.forEach((button) => {
        button.addEventListener('click', this._onPackClick);
      });

      this._productCards.forEach((button) => {
        button.addEventListener('click', this._onAddClick);
      });

      this._slotGrids?.forEach((grid) => {
        grid.addEventListener('click', this._onSlotClick);
      });
      this._ctaButtons.forEach((button) => {
        button.addEventListener('click', this._onCtaClick);
      });

      this._setPackSize(this._packSize, false);
      this._render();
      this._mountMobileBar();
      this._mobileBarMedia.addEventListener('change', this._onMobileBarMediaChange);
    }

    disconnectedCallback() {
      this._mobileBarMedia?.removeEventListener('change', this._onMobileBarMediaChange);
      this._restoreMobileBar();
      this._packButtons?.forEach((button) => {
        button.removeEventListener('click', this._onPackClick);
      });
      this._productCards?.forEach((button) => {
        button.removeEventListener('click', this._onAddClick);
      });
      this._slotGrids?.forEach((grid) => {
        grid.removeEventListener('click', this._onSlotClick);
      });
      this._ctaButtons?.forEach((button) => {
        button.removeEventListener('click', this._onCtaClick);
      });
    }

    _onMobileBarMediaChange() {
      this._mountMobileBar();
    }

    _mountMobileBar() {
      if (!this._mobileBar) return;

      const isMobile = this._mobileBarMedia.matches;

      if (isMobile) {
        if (!this._mobileBarHome) {
          this._mobileBarHome = this._mobileBar.parentElement;
        }

        if (this._mobileBar.parentElement !== document.body) {
          document.body.appendChild(this._mobileBar);
        }

        this._mobileBar.classList.add('is-visible');
        return;
      }

      this._restoreMobileBar();
      this._mobileBar.classList.remove('is-visible');
    }

    _restoreMobileBar() {
      if (!this._mobileBar || !this._mobileBarHome) return;
      if (this._mobileBar.parentElement === this._mobileBarHome) return;

      this._mobileBarHome.appendChild(this._mobileBar);
    }

    _onPackClick(event) {
      const button = event.currentTarget;
      const size = parseInt(button.dataset.packSize, 10);
      if (!size) return;
      this._setPackSize(size, true);
    }

    _setPackSize(size, trimSelections) {
      this._packSize = size;

      this._packButtons.forEach((button) => {
        const isActive = parseInt(button.dataset.packSize, 10) === size;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });

      if (trimSelections && this._selections.length > size) {
        this._selections = this._selections.slice(0, size);
      }

      this._render();
    }

    _onAddClick(event) {
      const button = event.currentTarget;
      const card = button.closest('[data-variant-id]');
      if (!card || button.disabled) return;

      if (this._selections.length >= this._packSize) return;

      this._selections.push({
        variantId: card.dataset.variantId,
        productId: card.dataset.productId,
        title: card.dataset.productTitle,
        subtitle: card.dataset.productSubtitle,
        image: card.dataset.productImage || ''
      });

      this._render();
    }

    _onSlotClick(event) {
      const removeButton = event.target.closest('[data-slot-remove]');
      if (!removeButton) return;

      const slot = removeButton.closest('[data-slot-index]');
      if (!slot || slot.classList.contains('is-empty')) return;

      const index = parseInt(slot.dataset.slotIndex, 10);
      if (Number.isNaN(index)) return;

      this._selections.splice(index, 1);
      this._render();
    }

    async _onCtaClick(event) {
      const button = event.currentTarget;
      if (button.getAttribute('aria-disabled') === 'true' || button.disabled) return;
      if (this._selections.length !== this._packSize) return;

      button.classList.add('is-loading');
      button.disabled = true;

      const items = this._selections.map((item) => ({
        id: parseInt(item.variantId, 10),
        quantity: 1,
        properties: {
          _bundle_pack: `${this._packSize}-Pack`
        }
      }));

      try {
        const response = await fetch(this._cartAddUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ items })
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.description || data.message || 'Unable to add bundle to cart.');
        }

        const cartAction = window.KROWN?.settings?.cart_action;
        if (cartAction === 'overlay' || cartAction === 'popup') {
          document.dispatchEvent(new CustomEvent('cart-updated'));
          if (typeof window.refreshCart === 'function') {
            window.refreshCart(true);
          } else {
            const cartSidebar = document.getElementById('site-cart-sidebar');
            if (cartSidebar && typeof cartSidebar.show === 'function') {
              cartSidebar.show();
            }
          }
        } else {
          window.location.href = window.KROWN?.settings?.routes?.cart_url || '/cart';
        }
      } catch (error) {
        alert(error.message);
      } finally {
        button.classList.remove('is-loading');
        button.disabled = false;
        button.setAttribute(
          'aria-disabled',
          this._selections.length !== this._packSize ? 'true' : 'false'
        );
      }
    }

    _render() {
      const remaining = this._packSize - this._selections.length;
      const ctaLabel =
        remaining > 0
          ? this.dataset.pickMoreLabel.replace('__COUNT__', remaining)
          : this.dataset.addBundleLabel;

      this._ctaButtons.forEach((button) => {
        const label = button.querySelector('[data-bundle-cta-label]');
        if (label) label.textContent = ctaLabel;
        const isReady = remaining === 0;
        button.disabled = false;
        button.setAttribute('aria-disabled', isReady ? 'false' : 'true');
        button.classList.toggle('is-ready', isReady);
      });

      if (!this._slotGrids?.length) return;

      this._slotGrids.forEach((slotGrid) => {
        const slots = slotGrid.querySelectorAll('[data-slot-index]');
        slots.forEach((slot, index) => {
          const item = this._selections[index];
          const isActiveSlot = index < this._packSize;
          const image = slot.querySelector('[data-slot-image]');

          slot.classList.toggle('is-hidden', !isActiveSlot);
          slot.classList.toggle('is-empty', isActiveSlot && !item);
          slot.classList.toggle('is-filled', isActiveSlot && !!item);

          const removeButton = slot.querySelector('[data-slot-remove]');
          if (removeButton) {
            removeButton.hidden = !item;
          }

          if (!image) return;

          if (item?.image) {
            image.src = item.image;
            image.alt = item.title || '';
            image.hidden = false;
          } else {
            image.removeAttribute('src');
            image.alt = '';
            image.hidden = true;
          }
        });
      });
    }
  }

  if (typeof customElements.get('flourish-bundle-builder') === 'undefined') {
    customElements.define('flourish-bundle-builder', FlourishBundleBuilder);
  }
}
